import { Link } from 'react-router-dom';
import { Camera, Sparkles, Palette, Download, Zap, Shield, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

export function LandingPage() {
  return (
    <>
      {/* Hero */}
      <section className="container relative py-20 md:py-32">
        <Badge variant="outline" className="mb-6 border-primary/40 text-primary">
          Built for car dealerships · Mobile-first
        </Badge>
        <h1 className="max-w-3xl text-5xl font-bold leading-[1.05] tracking-tight md:text-7xl">
          Dealership photos,
          <br />
          <span className="text-primary">studio quality in 90 seconds.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-lg text-muted-foreground md:text-xl">
          Walk the lot with your phone. BGSwap guides every shot, removes the background in the
          browser, and drops in your branded studio — ready to post the same trip.
        </p>
        <div className="mt-10 flex flex-col gap-3 sm:flex-row">
          <Button asChild size="lg" className="gap-2">
            <Link to="/login">
              Sign in <ChevronRight className="h-4 w-4" />
            </Link>
          </Button>
          <Button asChild size="lg" variant="outline">
            <a href="#workflow">See the workflow</a>
          </Button>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-4 md:grid-cols-3">
          <Stat n="20" label="guided shots per car" />
          <Stat n="~90s" label="from snap to posted" />
          <Stat n="100%" label="in your browser — no upload roulette" />
        </div>
      </section>

      {/* Workflow */}
      <section id="workflow" className="container py-16 md:py-24">
        <div className="mb-10 max-w-2xl">
          <h2 className="text-3xl font-bold tracking-tight md:text-4xl">The workflow</h2>
          <p className="mt-3 text-muted-foreground">
            Four steps. No desktop. No editing software.
          </p>
        </div>
        <ol className="grid gap-4 md:grid-cols-4">
          <Step n="1" title="Start a car">
            Stock # or VIN. We decode the year, make, model, trim from NHTSA in one tap.
          </Step>
          <Step n="2" title="Shoot the package">
            20 guided shots with live alignment overlays. Retake any step in place.
          </Step>
          <Step n="3" title="Browser cutout">
            Background removal runs right on the phone. Nothing leaves until it's clean.
          </Step>
          <Step n="4" title="Apply + export">
            Drop a branded studio and logo overlay. Export a ZIP for your DMS or post.
          </Step>
        </ol>
      </section>

      {/* Features */}
      <section className="container py-16 md:py-24">
        <h2 className="text-3xl font-bold tracking-tight md:text-4xl">Built for the lot, not the lab.</h2>
        <p className="mt-3 max-w-2xl text-muted-foreground">
          Every step fits a salesperson holding a phone in one hand and a key fob in the other.
        </p>
        <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <Feature icon={<Camera className="h-5 w-5" />} title="Guided capture">
            20 configurable shots with live alignment overlays — front, 3/4, wheels, interior,
            odometer. Retake any step in-place.
          </Feature>
          <Feature icon={<Sparkles className="h-5 w-5" />} title="Browser-side cutouts">
            Background removal runs on the phone — nothing sent anywhere until it's clean.
          </Feature>
          <Feature icon={<Palette className="h-5 w-5" />} title="Branded studios">
            Upload your own backgrounds and logo overlays. Re-theme any car later —
            Black Friday, summer sale, Halloween.
          </Feature>
          <Feature icon={<Shield className="h-5 w-5" />} title="Multi-location from day one">
            Tenants per dealership, strict data isolation, and a single login with per-location
            switching for owners.
          </Feature>
          <Feature icon={<Download className="h-5 w-5" />} title="One-click ZIP export">
            Originals, cutouts, and final composites bundled per car — ready for your DMS.
          </Feature>
          <Feature icon={<Zap className="h-5 w-5" />} title="Mobile PWA">
            Install to the home screen. Works on the lot with flaky service; catches up when back.
          </Feature>
        </div>
      </section>

      {/* Pricing */}
      <section className="container py-16 md:py-24" id="pricing">
        <div className="grid gap-8 md:grid-cols-3">
          <PriceCard plan="Starter" price="$99" note="per dealership / month"
            bullets={['100 active cars', '20-shot package', 'Browser bg-removal', 'ZIP export']} />
          <PriceCard plan="Pro" price="$249" highlight note="per dealership / month"
            bullets={['500 active cars', 'Unlimited backgrounds + overlays', 'Custom studio per tenant', 'Priority support']} />
          <PriceCard plan="Group" price="Talk to us" note="multi-rooftop"
            bullets={['Unlimited tenants', 'SSO / SAML', 'White-label option', 'Dedicated onboarding']} />
        </div>
      </section>

      {/* CTA */}
      <section className="container py-20">
        <Card className="mx-auto max-w-4xl border-primary/20 bg-gradient-to-br from-primary/10 via-transparent to-primary/5">
          <CardContent className="flex flex-col items-center gap-4 py-12 text-center">
            <h3 className="text-3xl font-bold tracking-tight">Walk the lot, ship the listing.</h3>
            <p className="max-w-xl text-muted-foreground">
              Ready to see it on your phone? Sign in with your dealership account and start a
              car session — first 100 free.
            </p>
            <Button asChild size="lg">
              <Link to="/login">Sign in</Link>
            </Button>
          </CardContent>
        </Card>
      </section>
    </>
  );
}

function Step({ n, title, children }: { n: string; title: string; children: React.ReactNode }) {
  return (
    <li className="relative rounded-xl border border-border bg-card p-5">
      <div className="absolute -top-3 left-5 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
        {n}
      </div>
      <div className="mt-2 font-semibold">{title}</div>
      <p className="mt-2 text-sm text-muted-foreground">{children}</p>
    </li>
  );
}

function Stat({ n, label }: { n: string; label: string }) {
  return (
    <Card>
      <CardContent className="py-6">
        <div className="text-3xl font-bold text-primary">{n}</div>
        <div className="mt-1 text-sm text-muted-foreground">{label}</div>
      </CardContent>
    </Card>
  );
}

function Feature({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <Card className="transition-colors hover:border-primary/40">
      <CardContent className="pt-6">
        <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
          {icon}
        </div>
        <div className="font-semibold">{title}</div>
        <p className="mt-2 text-sm text-muted-foreground">{children}</p>
      </CardContent>
    </Card>
  );
}

function PriceCard({ plan, price, note, bullets, highlight }: {
  plan: string; price: string; note: string; bullets: string[]; highlight?: boolean;
}) {
  return (
    <Card className={highlight ? 'border-primary/60 shadow-lg shadow-primary/10' : ''}>
      <CardContent className="flex flex-col gap-4 py-8">
        <div className="flex items-center gap-2">
          <div className="text-lg font-semibold">{plan}</div>
          {highlight && <Badge>Popular</Badge>}
        </div>
        <div className="flex items-baseline gap-1">
          <span className="text-4xl font-bold">{price}</span>
          <span className="text-sm text-muted-foreground">{note}</span>
        </div>
        <ul className="mt-2 space-y-2 text-sm">
          {bullets.map((b) => (
            <li key={b} className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              {b}
            </li>
          ))}
        </ul>
        <Button variant={highlight ? 'default' : 'outline'} className="mt-4">
          Start free
        </Button>
      </CardContent>
    </Card>
  );
}
